package com.isaias.fyattravel.web.home

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLImageElement

/**
 * Flights hero — safari & Uganda cultural-tourism collage.
 *
 * ONE unified block, split into three corner regions. Each region is its
 * own slideshow that fades through a themed set of images:
 *   Zone A "Nature"     — scenic landscapes only        (top-left)
 *   Zone B "Wildlife"   — safari animals only           (top-right)
 *   Zone C "Adventures" — waterfalls, rafting, culture   (wide bottom)
 *
 * The active image is painted as the region's CSS background, so only that
 * one photo is fetched at a time — the rest load lazily as their turn
 * arrives. Layout / wash / hover styling lives in `resources/index.html`
 * under `.fx-hero-*`. Images are LOCAL files under `resources/images/hero/`
 * — drop real photos in using the same filenames to replace them.
 */
@Composable
fun DynamicHeroSection(
    onSearchFlights: () -> Unit = {},
    onSearchHotels: () -> Unit = {}
) {
    Div({ classes("fx-hero-collage") }) {
        HeroZone(
            zoneClass = "fx-hero-zone-a",
            images = NATURE,
            intervalMs = 5200,
            startDelayMs = 0
        )
        HeroZone(
            zoneClass = "fx-hero-zone-b",
            images = WILDLIFE,
            intervalMs = 5200,
            startDelayMs = 1700
        )
        HeroZone(
            zoneClass = "fx-hero-zone-c",
            images = ADVENTURES,
            intervalMs = 4300,
            startDelayMs = 3300
        )
    }
}

/** One corner region: fades through its themed images on a timer. */
@Composable
private fun HeroZone(
    zoneClass: String,
    images: List<HeroImage>,
    intervalMs: Int,
    startDelayMs: Int
) {
    var index by remember { mutableStateOf(0) }
    var dim by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        var intervalId = 0
        val timeoutId = window.setTimeout({
            intervalId = window.setInterval({
                // brief fade-out, swap image, fade back in
                dim = true
                window.setTimeout({
                    index = (index + 1) % images.size
                    dim = false
                }, 260)
            }, intervalMs)
        }, startDelayMs)
        onDispose {
            window.clearTimeout(timeoutId)
            window.clearInterval(intervalId)
        }
    }

    // Warm the browser cache with the next photo so the swap paints instantly.
    LaunchedEffect(index) {
        val next = images[(index + 1) % images.size]
        (document.createElement("img") as HTMLImageElement).src = next.src
    }

    val current = images[index]

    Div({
        classes("fx-hero-zone", zoneClass)
        // expose the photo to assistive tech / SEO even though it's a CSS bg
        attr("role", "img")
        attr("aria-label", current.alt)
        style {
            property("background-image", "url(${current.src})")
            opacity(if (dim) 0.45 else 1.0)
        }
    }) {
        Span({ classes("fx-hero-caption") }) { Text(current.caption) }
    }
}

private data class HeroImage(val src: String, val alt: String, val caption: String)

// Zone A — Nature & Landscapes (scenic only: no animals, people, structures)
private val NATURE = listOf(
    HeroImage(
        "images/hero/nature/nature-1.jpg",
        "Morning light and mist over forested hills and banana plantations in western Uganda",
        "Golden Hour Uganda"
    ),
    HeroImage(
        "images/hero/nature/nature-2.jpg",
        "A crater lake ringed by green hills in western Uganda",
        "Breathtaking Views"
    ),
    HeroImage(
        "images/hero/nature/nature-3.jpg",
        "A red-earth road winding past a lone tree toward distant mountains in Uganda",
        "Where the Wild Begins"
    )
)

// Zone B — Wildlife & Animals (safari animals only)
private val WILDLIFE = listOf(
    HeroImage(
        "images/hero/animals/animals-1.jpg",
        "Silverback mountain gorilla among green foliage in Uganda",
        "Gorilla Trekking"
    ),
    HeroImage(
        "images/hero/animals/animals-2.jpg",
        "African elephant walking through golden savanna grass at sunrise",
        "Big Five Safari"
    ),
    HeroImage(
        "images/hero/animals/animals-3.jpg",
        "Zebras grazing in tall golden grass in warm evening light",
        "Savanna at Dusk"
    )
)

// Zone C — Adventures & Special Places (widest region)
private val ADVENTURES = listOf(
    HeroImage(
        "images/hero/adventures/adventures-1.jpg",
        "Rainbow over the Nile crashing through the gorge at Murchison Falls, Uganda",
        "Murchison Falls"
    ),
    HeroImage(
        "images/hero/adventures/adventures-2.jpg",
        "Rafting team paddling through white-water rapids in a rocky river gorge",
        "White-Water Rafting"
    ),
    HeroImage(
        "images/hero/adventures/adventures-3.jpg",
        "Ugandan dancers in orange dress and ostrich-feather headdresses performing a traditional dance",
        "Cultural Village Visit"
    ),
    HeroImage(
        "images/hero/adventures/adventures-4.jpg",
        "Trekkers hiking a trail through dense rainforest lit by shafts of sunlight",
        "Rainforest Trek"
    )
)
